import {
  ArrowUpRight,
  BookOpenText,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  CornerDownLeft,
  GitMerge,
  GripVertical,
  Link2,
  Lightbulb,
  MoveRight,
  Plus,
  Send,
  Trash2,
  Unlink,
  X,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { DesktopClient } from "@orni/domain-typescript";
import type { Session } from "../domain";
import type {
  NoteBlock,
  NoteBlockAction,
  NoteBook,
  NoteBookDetail,
} from "../sdk/note-types";
import { localizeError, useI18n } from "../i18n";
import type { Translate } from "../i18n-core";
import type { TranslationKey } from "../locales/en";
import { ProviderIcon } from "./ProviderIcon";

const NOTE_DRAFT_PREFIX = "orni.notes.v1";
export const NOTES_CHANGED_EVENT = "orni:notes-changed";
const PANEL_PAGE_SIZE = 6;

export function notifyNotesChanged() {
  window.dispatchEvent(new Event(NOTES_CHANGED_EVENT));
}

function readDraft(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeDraft(key: string, value: string) {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // Draft persistence is best effort when browser storage is unavailable.
  }
}

export function noteTitle(note: NoteBook, t: Translate): string {
  if (note.isInbox) return t("notes.inbox");
  return note.name || note.latestBlockFirstLine || t("notes.untitled");
}

export function useNoteDraft(key: string, fallback = "") {
  const [value, setValueState] = useState(() => readDraft(key) ?? fallback);
  useEffect(() => setValueState(readDraft(key) ?? fallback), [fallback, key]);
  const setValue = (next: string) => {
    setValueState(next);
    writeDraft(key, next);
  };
  const clear = () => {
    setValueState("");
    writeDraft(key, "");
  };
  return { value, setValue, clear };
}

export function fitTextarea(element: HTMLTextAreaElement | null) {
  if (!element) return;
  element.style.height = "auto";
  element.style.height = `${Math.min(280, Math.max(element.scrollHeight, 48))}px`;
}

function fitBlockTextarea(element: HTMLTextAreaElement | null) {
  if (!element) return;
  element.style.height = "auto";
  const lineHeight = Number.parseFloat(window.getComputedStyle(element).lineHeight) || 22;
  const compactHeight = lineHeight * 2;
  const contentHeight = element.scrollHeight;
  element.style.height = `${Math.max(48, contentHeight > compactHeight + 1
    ? contentHeight + lineHeight * 2
    : contentHeight)}px`;
}

function textOffsetAtPoint(container: HTMLElement, clientX: number, clientY: number) {
  const caretDocument = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  const position = caretDocument.caretPositionFromPoint?.(clientX, clientY);
  const fallbackRange = position ? null : caretDocument.caretRangeFromPoint?.(clientX, clientY);
  const node = position?.offsetNode ?? fallbackRange?.startContainer;
  const offset = position?.offset ?? fallbackRange?.startOffset;
  if (!node || offset === undefined || !container.contains(node)) return null;
  const range = document.createRange();
  range.selectNodeContents(container);
  try {
    range.setEnd(node, offset);
  } catch {
    return null;
  }
  return range.toString().length;
}

export function NoteComposer({
  draftKey,
  placeholder,
  onSave,
  compact = false,
  actionsInHeader = false,
  autoFocus = false,
}: {
  draftKey: string;
  placeholder: string;
  onSave: (content: string) => Promise<boolean>;
  compact?: boolean;
  actionsInHeader?: boolean;
  autoFocus?: boolean;
}) {
  const { t } = useI18n();
  const draft = useNoteDraft(draftKey);
  const ref = useRef<HTMLTextAreaElement>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => fitTextarea(ref.current), [draft.value]);
  useEffect(() => {
    if (!autoFocus) return;
    const frame = window.requestAnimationFrame(() => ref.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [autoFocus, draftKey]);
  const save = async () => {
    const value = draft.value.trim();
    if (!value || saving) return;
    setSaving(true);
    setError("");
    try {
      if (await onSave(value)) {
        draft.clear();
        if (ref.current) ref.current.style.height = "";
      }
    } catch (cause) {
      setError(localizeError(cause, t, "notes.saveFailed"));
    } finally {
      setSaving(false);
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const shortcut = event.metaKey || event.ctrlKey;
    if (
      event.nativeEvent.isComposing
      || !shortcut
      || (event.key !== "Enter" && event.key.toLowerCase() !== "s")
    ) return;
    event.preventDefault();
    void save();
  };
  return (
    <div className={`note-composer ${compact ? "compact" : ""} ${actionsInHeader ? "actions-in-header" : ""}`}>
      <textarea
        ref={ref}
        autoFocus={autoFocus}
        rows={2}
        value={draft.value}
        placeholder={placeholder}
        onChange={(event) => draft.setValue(event.target.value)}
        onInput={(event) => fitTextarea(event.currentTarget)}
        onKeyDown={onKeyDown}
      />
      <div className="note-composer-actions">
        {draft.value ? (
          <button type="button" onClick={draft.clear} title={t("common.clear")}>
            <X size={14} />
          </button>
        ) : null}
        <button
          className="note-composer-save"
          type="button"
          disabled={!draft.value.trim() || saving}
          onClick={() => void save()}
          title={t("common.save")}
        >
          {saving ? <span className="spinner" /> : <Check size={14} />}
          <span>{t("common.save")}</span>
        </button>
      </div>
      {error ? <small>{error}</small> : null}
    </div>
  );
}

export function NoteBlockCard({
  client,
  block,
  highlighted,
  onSaved,
  onAction,
  onInsert,
  onSend,
  sendDisabled = false,
  dragging = false,
  dropPosition,
  onReorderStart,
  compact = false,
}: {
  client: DesktopClient;
  block: NoteBlock;
  highlighted?: boolean;
  onSaved: (detail: NoteBookDetail) => void;
  onAction?: (action: NoteBlockAction, block: NoteBlock) => void;
  onInsert?: (content: string) => void;
  onSend?: (block: NoteBlock) => void;
  sendDisabled?: boolean;
  dragging?: boolean;
  dropPosition?: "before" | "after";
  onReorderStart?: (event: ReactPointerEvent<HTMLElement>, block: NoteBlock) => void;
  compact?: boolean;
}) {
  const { t, formatDateTime } = useI18n();
  const [editing, setEditing] = useState(false);
  const key = `${NOTE_DRAFT_PREFIX}.edit.${block.id}`;
  const [draft, setDraft] = useState(block.content);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLParagraphElement>(null);
  const pendingCaretRef = useRef<number | null>(null);
  const beginEdit = (event: ReactMouseEvent<HTMLElement>) => {
    const nextDraft = readDraft(key) ?? block.content;
    const clickedOffset = contentRef.current
      ? textOffsetAtPoint(contentRef.current, event.clientX, event.clientY)
      : null;
    pendingCaretRef.current = clickedOffset === null
      ? nextDraft.length
      : Math.min(clickedOffset, nextDraft.length);
    setDraft(nextDraft);
    setEditing(true);
  };
  const copyContent = async () => {
    try {
      await navigator.clipboard.writeText(block.content);
    } catch {
      const field = document.createElement("textarea");
      field.value = block.content;
      field.style.position = "fixed";
      field.style.opacity = "0";
      document.body.appendChild(field);
      field.select();
      document.execCommand("copy");
      field.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_600);
  };
  const deleteBlock = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      const result = await client.deleteNoteBlock(block.id, block.revision);
      setDeleteConfirm(false);
      onSaved(result.note);
    } catch (cause) {
      setError(localizeError(cause, t, "notes.actionFailed"));
    } finally {
      setSaving(false);
    }
  };
  useEffect(() => {
    if (!editing || !ref.current) return;
    fitBlockTextarea(ref.current);
    const offset = pendingCaretRef.current;
    if (offset !== null) {
      ref.current.focus();
      ref.current.setSelectionRange(offset, offset);
      pendingCaretRef.current = null;
    }
  }, [draft, editing]);
  const save = async () => {
    const value = draft.trim();
    if (saving || !value) return;
    if (value === block.content) {
      writeDraft(key, "");
      setEditing(false);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await client.updateNoteBlock(block.id, value, block.revision);
      writeDraft(key, "");
      setEditing(false);
      onSaved(result.note);
    } catch (cause) {
      setError(localizeError(cause, t, "notes.saveFailed"));
    } finally {
      setSaving(false);
    }
  };
  if (editing) {
    return (
      <article className={`note-block editing ${compact ? "compact" : ""}`}>
        <textarea
          ref={ref}
          autoFocus
          rows={2}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            writeDraft(key, event.target.value);
          }}
          onInput={(event) => fitBlockTextarea(event.currentTarget)}
          onBlur={(event: FocusEvent<HTMLTextAreaElement>) => {
            if (!event.currentTarget.parentElement?.contains(event.relatedTarget)) void save();
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              writeDraft(key, "");
              setDraft(block.content);
              setEditing(false);
            } else if (
              (event.metaKey || event.ctrlKey)
              && (event.key === "Enter" || event.key.toLowerCase() === "s")
            ) {
              event.preventDefault();
              void save();
            }
          }}
        />
        <button type="button" disabled={saving || !draft.trim()} onClick={() => void save()} title={t("common.save")}>
          {saving ? <span className="spinner" /> : <Check size={14} />}
        </button>
        {error ? <small>{error}</small> : null}
      </article>
    );
  }
  return (
    <article
      className={`note-block ${highlighted ? "just-merged" : ""} ${compact ? "compact" : ""} ${dragging ? "dragging" : ""} ${dropPosition ? `drop-${dropPosition}` : ""}`}
      data-note-block-id={block.id}
      onClick={beginEdit}
    >
      <p ref={contentRef}>{block.content}</p>
      <footer>
        {onReorderStart ? (
          <span
            className="note-drag-handle"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event: ReactPointerEvent<HTMLSpanElement>) => {
              event.preventDefault();
              event.stopPropagation();
              onReorderStart(event, block);
            }}
            title={t("notes.reorder")}
          ><GripVertical size={13} /></span>
        ) : null}
        <time>{formatDateTime(block.updatedAt)}</time>
        {block.mergedAt ? (
          <span title={formatDateTime(block.mergedAt)}>
            <GitMerge size={11} />
            {t("notes.mergedFrom", { source: block.mergeSourceTitle || t("notes.untitled") })}
          </span>
        ) : null}
        <div onClick={(event) => event.stopPropagation()}>
          {deleteConfirm ? (
            <span className="note-delete-inline">
              <small>{t("notes.deleteBlockConfirm")}</small>
              <button type="button" onClick={() => setDeleteConfirm(false)}>{t("common.cancel")}</button>
              <button className="danger" type="button" disabled={saving} onClick={() => void deleteBlock()}>{t("common.delete")}</button>
            </span>
          ) : (
            <>
              <button className={copied ? "copied" : ""} type="button" onClick={() => void copyContent()} title={copied ? t("notes.copied") : t("notes.copy")}>
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
              {onInsert ? (
                <button type="button" onClick={() => onInsert(block.content)} title={t("notes.insertIntoSession")}>
                  <CornerDownLeft size={14} />
                </button>
              ) : null}
              {onSend ? (
                <button
                  type="button"
                  disabled={sendDisabled}
                  onClick={() => onSend(block)}
                  title={sendDisabled ? t("notes.linkBeforeSending") : t("notes.sendBlock")}
                >
                  <Send size={14} />
                </button>
              ) : null}
              {onAction ? (
                <>
                  <button type="button" onClick={() => onAction("move", block)} title={t("notes.move")}>
                    <MoveRight size={14} />
                  </button>
                  <button type="button" onClick={() => onAction("merge", block)} title={t("notes.merge")}>
                    <GitMerge size={14} />
                  </button>
                </>
              ) : null}
              <button className="danger" type="button" onClick={() => setDeleteConfirm(true)} title={t("notes.deleteBlock")}>
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </footer>
      {error ? <small className="note-block-error">{error}</small> : null}
    </article>
  );
}

function noteSessionProvider(session: Session): "codex" | "claude" | "opencode" {
  if (session.provider === "Claude Code") return "claude";
  if (session.provider === "OpenCode") return "opencode";
  return "codex";
}

export function NoteSessionDialog({
  sessions,
  currentSessionId,
  onAssociate,
  onCreate,
  onClose,
}: {
  sessions: Session[];
  currentSessionId?: string;
  onAssociate: (sessionId?: string) => Promise<void>;
  onCreate: () => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const selectableSessions = sessions.filter((session) => session.workRole !== "execution");
  const associate = async (sessionId?: string) => {
    if (pendingSessionId !== null) return;
    setPendingSessionId(sessionId ?? "unlink");
    setError("");
    try {
      await onAssociate(sessionId);
      onClose();
    } catch (cause) {
      setError(localizeError(cause, t, "notes.associationFailed"));
      setPendingSessionId(null);
    }
  };
  return (
    <div className="dialog-backdrop note-dialog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="note-transfer-dialog note-session-dialog" role="dialog" aria-modal="true">
        <header>
          <span><Link2 size={16} /></span>
          <div>
            <h2>{t("notes.associateSession")}</h2>
            <p>{t("notes.associateSessionDetail")}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t("common.close")}><X size={15} /></button>
        </header>
        <button className="note-create-session-row" type="button" onClick={onCreate}>
          <i><Plus size={15} /></i>
          <span>
            <strong>{t("notes.createAndAssociate")}</strong>
            <small>{t("notes.createAndAssociateDetail")}</small>
          </span>
          <ChevronRight size={15} />
        </button>
        <label>
          <span>{t("notes.existingSessions")}</span>
          <div className="note-target-grid note-session-targets">
            {selectableSessions.map((session) => (
              <button
                className={currentSessionId === session.id ? "selected" : ""}
                type="button"
                disabled={pendingSessionId !== null}
                key={session.id}
                onClick={() => void associate(session.id)}
              >
                <i><ProviderIcon provider={noteSessionProvider(session)} size={15} /></i>
                <span>
                  <strong>{session.title}</strong>
                  <small>
                    {session.managementMode === "orni_work" ? t("profile.orni") : t("profile.native")}
                    {" · "}
                    {t(`status.${session.state}` as TranslationKey)}
                  </small>
                </span>
                {pendingSessionId === session.id ? <span className="spinner" /> : currentSessionId === session.id ? <Check size={14} /> : null}
              </button>
            ))}
            {!selectableSessions.length ? <p>{t("notes.noSessionsAvailable")}</p> : null}
          </div>
        </label>
        {error ? <p className="note-dialog-error">{error}</p> : null}
        <footer>
          {currentSessionId ? (
            <button type="button" disabled={pendingSessionId !== null} onClick={() => void associate()}>
              {pendingSessionId === "unlink" ? <span className="spinner" /> : <Unlink size={13} />}
              {t("notes.removeAssociation")}
            </button>
          ) : <span />}
          <button type="button" onClick={onClose}>{t("common.cancel")}</button>
        </footer>
      </section>
    </div>
  );
}

export type NoteSessionAction =
  | { type: "open" }
  | { type: "send_note" }
  | { type: "send_block"; block: NoteBlock };

export function NoteSessionActionDialog({
  action,
  session,
  note,
  relatedNotes,
  onClose,
  onConfirm,
}: {
  action: NoteSessionAction;
  session: Session;
  note: NoteBookDetail;
  relatedNotes: NoteBook[];
  onClose: () => void;
  onConfirm: (selectedIds: string[]) => Promise<void>;
}) {
  const { t } = useI18n();
  const requiredId = action.type === "send_block" ? action.block.id : note.note.id;
  const [selectedIds, setSelectedIds] = useState(() => new Set([requiredId]));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const isOpen = action.type === "open";
  const candidates = action.type === "send_note"
    ? relatedNotes
    : action.type === "send_block"
      ? note.blocks
      : [];
  const toggle = (id: string) => {
    if (id === requiredId) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const confirm = async () => {
    if (pending) return;
    setPending(true);
    setError("");
    try {
      await onConfirm([...selectedIds]);
    } catch (cause) {
      setError(localizeError(cause, t, "notes.sendFailed"));
      setPending(false);
    }
  };
  const title = isOpen
    ? t("notes.confirmOpenTitle")
    : action.type === "send_note"
      ? t("notes.confirmSendNoteTitle")
      : t("notes.confirmSendBlockTitle");
  return (
    <div className="dialog-backdrop note-dialog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !pending) onClose();
    }}>
      <section className="note-transfer-dialog note-session-action-dialog" role="dialog" aria-modal="true">
        <header>
          <span>{isOpen ? <ArrowUpRight size={16} /> : <Send size={16} />}</span>
          <div>
            <h2>{title}</h2>
            <p>{isOpen ? t("notes.confirmOpenDetail") : t("notes.confirmSendDetail")}</p>
          </div>
          <button type="button" disabled={pending} onClick={onClose} aria-label={t("common.close")}><X size={15} /></button>
        </header>
        <div className="note-session-action-target">
          <ProviderIcon provider={noteSessionProvider(session)} size={16} />
          <span>
            <small>{t("notes.destinationSession")}</small>
            <strong>{session.title}</strong>
          </span>
        </div>
        {!isOpen ? (
          <label>
            <span>{action.type === "send_note" ? t("notes.selectNotebooks") : t("notes.selectBlocks")}</span>
            <div className={`note-target-grid ${action.type === "send_block" ? "note-send-block-targets" : ""}`}>
              {candidates.map((candidate) => {
                const id = candidate.id;
                const selected = selectedIds.has(id);
                const fixed = id === requiredId;
                const candidateTitle = action.type === "send_note"
                  ? noteTitle(candidate as NoteBook, t)
                  : (candidate as NoteBlock).content;
                return (
                  <button
                    className={`${selected ? "selected" : ""} ${fixed ? "fixed" : ""}`}
                    type="button"
                    aria-pressed={selected}
                    aria-disabled={fixed}
                    key={id}
                    onClick={() => toggle(id)}
                  >
                    <i>{action.type === "send_note" ? <BookOpenText size={14} /> : <Lightbulb size={14} />}</i>
                    <span>
                      <strong>{candidateTitle}</strong>
                      <small>{fixed
                        ? t("notes.currentSelection")
                        : selected
                          ? t("notes.included")
                          : t("notes.notIncluded")}</small>
                    </span>
                    {selected ? <Check size={14} /> : null}
                  </button>
                );
              })}
            </div>
          </label>
        ) : null}
        {error ? <p className="note-dialog-error">{error}</p> : null}
        <footer>
          <button type="button" disabled={pending} onClick={onClose}>{t("common.cancel")}</button>
          <button className="primary-button" type="button" disabled={pending} onClick={() => void confirm()}>
            {pending ? <span className="spinner" /> : isOpen ? t("notes.confirmOpen") : t("notes.confirmSend")}
          </button>
        </footer>
      </section>
    </div>
  );
}

export function NoteTransferDialog({
  client,
  notes,
  action,
  sourceBlock,
  sourceNote,
  onClose,
  onComplete,
}: {
  client: DesktopClient;
  notes: NoteBook[];
  action: NoteBlockAction | "merge_note";
  sourceBlock?: NoteBlock;
  sourceNote: NoteBook;
  onClose: () => void;
  onComplete: (detail: NoteBookDetail, affectedBlockIds: string[]) => void;
}) {
  const { t } = useI18n();
  const targets = notes.filter((note) => action !== "merge_note" || note.id !== sourceNote.id);
  const [targetNoteId, setTargetNoteId] = useState(
    action === "merge" ? sourceNote.id : targets[0]?.id ?? "",
  );
  const [targetDetail, setTargetDetail] = useState<NoteBookDetail | null>(null);
  const [targetBlockId, setTargetBlockId] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const title = action === "move"
    ? t("notes.action.move")
    : action === "merge"
      ? t("notes.action.merge")
      : t("notes.action.merge_note");
  useEffect(() => {
    if (action !== "merge" || !targetNoteId) return;
    setTargetBlockId("");
    void client.getNote(targetNoteId).then(setTargetDetail).catch((cause) => {
      setError(localizeError(cause, t, "notes.loadFailed"));
    });
  }, [action, client, t, targetNoteId]);
  const submit = async () => {
    if (!targetNoteId || pending || (action === "merge" && !targetBlockId)) return;
    setPending(true);
    setError("");
    try {
      const result = action === "merge_note"
        ? await client.mergeNote(sourceNote.id, targetNoteId, sourceNote.revision)
        : await client.actOnNoteBlock(sourceBlock!.id, {
            action,
            targetNoteId,
            targetBlockId: action === "merge" ? targetBlockId : undefined,
            expectedRevision: sourceBlock!.revision,
          });
      onComplete(result.note, result.affectedBlockIds);
    } catch (cause) {
      setError(localizeError(cause, t, "notes.actionFailed"));
    } finally {
      setPending(false);
    }
  };
  return (
    <div className="dialog-backdrop note-dialog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="note-transfer-dialog" role="dialog" aria-modal="true">
        <header>
          <span>{action === "move" ? <MoveRight size={16} /> : <GitMerge size={16} />}</span>
          <div>
            <h2>{title}</h2>
            <p>{t("notes.action.chooseTarget")}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t("common.close")}><X size={15} /></button>
        </header>
        <label>
          <span>{t("notes.notebook")}</span>
          <div className="note-target-grid">
            {targets.map((note) => (
              <button
                className={targetNoteId === note.id ? "selected" : ""}
                type="button"
                key={note.id}
                onClick={() => setTargetNoteId(note.id)}
              >
                <i><BookOpenText size={14} /></i>
                <span>
                  <strong>{noteTitle(note, t)}</strong>
                  <small>{note.blockCount ? t("notes.blockCount", { count: note.blockCount }) : t("notes.emptyShort")}</small>
                </span>
                {targetNoteId === note.id ? <Check size={14} /> : null}
              </button>
            ))}
          </div>
        </label>
        {action === "merge" ? (
          <label>
            <span>{t("notes.targetBlock")}</span>
            <div className="note-target-blocks">
              {targetDetail?.blocks.filter((block) => block.id !== sourceBlock?.id).map((block) => (
                <button
                  className={targetBlockId === block.id ? "selected" : ""}
                  type="button"
                  key={block.id}
                  onClick={() => setTargetBlockId(block.id)}
                >
                  <span>{block.content}</span>
                  {targetBlockId === block.id ? <Check size={14} /> : null}
                </button>
              ))}
              {targetDetail && !targetDetail.blocks.some((block) => block.id !== sourceBlock?.id) ? (
                <p>{t("notes.noMergeTarget")}</p>
              ) : null}
            </div>
          </label>
        ) : null}
        {error ? <p className="note-dialog-error">{error}</p> : null}
        <footer>
          <button type="button" onClick={onClose}>{t("common.cancel")}</button>
          <button className="primary-button" type="button" disabled={!targetNoteId || pending || (action === "merge" && !targetBlockId)} onClick={() => void submit()}>
            {pending ? <span className="spinner" /> : t("notes.confirmAction")}
          </button>
        </footer>
      </section>
    </div>
  );
}

export function SessionNotesPanel({
  client,
  session,
  sessions,
  onClose,
  onInsert,
}: {
  client: DesktopClient;
  session: Session;
  sessions: Session[];
  onClose: () => void;
  onInsert: (content: string) => void;
}) {
  const { t } = useI18n();
  const [notes, setNotes] = useState<NoteBook[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState<NoteBookDetail | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [error, setError] = useState("");
  const coordinatorId = session.workRole === "coordinator"
    ? session.id
    : session.coordinatorSessionId;
  const sessionIds = useMemo(() => new Set(
    coordinatorId
      ? sessions
          .filter((candidate) => (
            candidate.id === coordinatorId
            || candidate.coordinatorSessionId === coordinatorId
          ))
          .map((candidate) => candidate.id)
      : [session.id],
  ), [coordinatorId, session.id, sessions]);
  const sessionScopeKey = [...sessionIds].sort().join("\n");
  const refreshList = async (preferred?: string) => {
    const allNotes = await client.listNotes();
    const result = allNotes.filter((note) => (
      note.isInbox || Boolean(note.sessionId && sessionIds.has(note.sessionId))
    ));
    setNotes(result);
    setSelectedId((current) => preferred || (result.some((note) => note.id === current) ? current : result[0]?.id ?? ""));
  };
  useEffect(() => {
    void refreshList().catch((cause) => setError(localizeError(cause, t, "notes.loadFailed")));
  }, [client, sessionScopeKey, t]);
  useEffect(() => {
    const refresh = () => {
      void refreshList(selectedId).then(() => {
        if (selectedId) return client.getNote(selectedId).then(setDetail);
      }).catch((cause) => setError(localizeError(cause, t, "notes.loadFailed")));
    };
    window.addEventListener(NOTES_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(NOTES_CHANGED_EVENT, refresh);
  }, [client, selectedId, sessionScopeKey, t]);
  useEffect(() => {
    setPage(0);
    if (!selectedId) {
      setDetail(null);
      return;
    }
    void client.getNote(selectedId).then(setDetail).catch((cause) => setError(localizeError(cause, t, "notes.loadFailed")));
  }, [client, selectedId, t]);
  useEffect(() => {
    if (!pickerOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setPickerOpen(false);
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setPickerOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [pickerOpen]);
  const ensureSelectedNote = async () => {
    if (detail) return detail;
    const created = await client.createNote({ sessionId: session.id });
    await refreshList(created.note.id);
    setDetail(created);
    return created;
  };
  const save = async (content: string) => {
    const target = await ensureSelectedNote();
    const result = await client.createNoteBlock(target.note.id, content, target.note.revision);
    setDetail(result.note);
    await refreshList(target.note.id);
    setPage(0);
    return true;
  };
  const pages = Math.max(1, Math.ceil((detail?.blocks.length ?? 0) / PANEL_PAGE_SIZE));
  const visibleBlocks = detail?.blocks.slice(page * PANEL_PAGE_SIZE, (page + 1) * PANEL_PAGE_SIZE) ?? [];
  return (
    <aside className="session-notes-panel">
      <header>
        <div className="note-panel-picker" ref={pickerRef}>
          <button type="button" onClick={() => setPickerOpen((value) => !value)}>
            <i><Lightbulb size={14} /></i>
            <span>{detail ? noteTitle(detail.note, t) : t("notes.title")}</span>
            <ChevronDown size={14} />
          </button>
          {pickerOpen ? (
            <div>
              {notes.map((note) => (
                <button type="button" key={note.id} onClick={() => {
                  setSelectedId(note.id);
                  setPickerOpen(false);
                }}>{noteTitle(note, t)}</button>
              ))}
              <button type="button" onClick={() => void client.createNote({ sessionId: session.id }).then((created) => {
                setDetail(created);
                void refreshList(created.note.id);
                setPickerOpen(false);
              })}><Plus size={13} />{t("notes.new")}</button>
            </div>
          ) : null}
        </div>
        <button type="button" onClick={onClose} aria-label={t("common.close")}><X size={15} /></button>
      </header>
      <NoteComposer
        compact
        draftKey={`${NOTE_DRAFT_PREFIX}.session.${session.id}.${selectedId || "new"}`}
        placeholder={t("notes.capturePlaceholder")}
        onSave={save}
      />
      <div className="session-note-blocks">
        {visibleBlocks.map((block) => (
          <NoteBlockCard
            client={client}
            compact
            block={block}
            key={block.id}
            onSaved={(next) => {
              setDetail(next);
              void refreshList(next.note.id);
            }}
            onInsert={detail?.note.isInbox ? undefined : onInsert}
          />
        ))}
        {detail && !detail.blocks.length ? <p>{t("notes.empty")}</p> : null}
        {error ? <p className="note-panel-error">{error}</p> : null}
      </div>
      {pages > 1 ? (
        <footer>
          <button type="button" disabled={page === 0} onClick={() => setPage((value) => value - 1)}><ChevronLeft size={14} /></button>
          <span>{page + 1} / {pages}</span>
          <button type="button" disabled={page + 1 >= pages} onClick={() => setPage((value) => value + 1)}><ChevronRight size={14} /></button>
        </footer>
      ) : null}
    </aside>
  );
}

export function QuickNotePopover({
  client,
  onSaved,
}: {
  client: DesktopClient;
  onSaved?: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="rail-note-popover">
      <header><strong>{t("notes.quickCapture")}</strong></header>
      <NoteComposer
        compact
        actionsInHeader
        draftKey={`${NOTE_DRAFT_PREFIX}.quick`}
        placeholder={t("notes.capturePlaceholder")}
        onSave={async (content) => {
          await client.createQuickNote(content);
          notifyNotesChanged();
          onSaved?.();
          return true;
        }}
      />
    </div>
  );
}
